import { isValid, parseISO } from "date-fns";
import {
    NativeInvoiceDraftBody,
    NativeInvoiceUpdateBody,
} from "@common/schemas/domains/invoice/native-invoice.schema";
import { Entity } from "@common/types/entity";
import { InvoiceStatus } from "@common/types/invoice";
import { NativeInvoiceTermType } from "@common/helpers/invoice-terms.helper";
import { isKnownNonUsInvoiceCountry } from "@common/helpers/nativeInvoice";
import { NativeInvoiceLineRenderSnapshot } from "@common/types/nativeInvoice";
import { getInitials } from "./getInitials";
import { buildNativeInvoiceLineAccounting } from "./buildNativeInvoiceLineAccounting";
import {
    computeNativeInvoiceLineAmount,
    computeNativeInvoiceTotals,
} from "./computeNativeInvoiceTotals";
import { storedLineAdjustmentFromLabeled } from "./nativeInvoicePerLineAdjustment";
import {
    EMAIL_PATTERN,
    invoiceToPartyBusinessName,
    nonEmptyIdOrUndefined,
    nonEmptyStringOrNull,
    parseRecipientList,
} from "./nativeInvoiceFormHelpers";
import {
    NativeInvoiceFormState,
    NativeInvoiceLineCodingByLineId,
} from "./nativeInvoiceFormTypes";

// Safely converts a user-entered datetime string to an ISO string, returning
// null for empty or unparseable input so an invalid/partial date can't throw
// while building the draft body (which would break the drawer).
function toIsoStringOrNull(value: string | null | undefined): string | null {
    if (!value) {
        return null;
    }
    // toISOString (UTC "Z") rather than formatISO (local offset): the email
    // snapshot schema validates with z.string().datetime(), which only accepts
    // the "Z" suffix.
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed.toISOString() : null;
}

type InvoiceSnapshotFontFamily =
    | "inter"
    | "arial"
    | "helvetica"
    | "georgia"
    | "times"
    | "courier";

function toSnapshotFontFamily(
    fontFamily: string | null,
): InvoiceSnapshotFontFamily | null {
    switch (fontFamily) {
        case "inter":
        case "arial":
        case "helvetica":
        case "georgia":
        case "times":
        case "courier":
            return fontFamily;
        default:
            return null;
    }
}

// Serialize the resolved form state into the native-invoice draft payload (the
// create/update API body). Pure so the mapping — email-snapshot gating,
// catalog write-back fields, adjustment signs — can be unit-tested without
// mounting the drawer hook. Returns null until an entity is available to scope
// the invoice.
export function buildNativeInvoiceDraftBody({
    state,
    defaultEntity,
    primaryAccrualLedgerId,
    incomeAccountLedgerId,
    lineCodingBaselineByLineId,
    isTemplateMode,
}: {
    state: NativeInvoiceFormState;
    defaultEntity?: Entity;
    primaryAccrualLedgerId?: string;
    incomeAccountLedgerId?: string;
    lineCodingBaselineByLineId: NativeInvoiceLineCodingByLineId;
    // In template mode the invoice/due dates are discarded server-side
    // (NativeInvoiceDocumentCopyInternalService#persistPrototypeContent nulls
    // invoiceDate/due/dueDateOnly) and recomputed from Terms when the template
    // is applied. Terms stays editable, so a "due by the Nth day of the current
    // month" custom term can derive a due date before the invoice date. The
    // date pickers are hidden here (see DetailsPanel), so the user can't correct
    // it — omit the due date so the render-snapshot due-date rule
    // (nativeInvoiceRenderSnapshotInputSchema) can't reject an otherwise valid
    // template save with a generic "Save failed".
    isTemplateMode?: boolean;
}): NativeInvoiceDraftBody | null {
    if (!defaultEntity) {
        return null;
    }

    const {
        subtotal,
        taxAmount,
        discountAmount,
        depositAmount,
        taxSign,
        discountSign,
        depositSign,
        total,
    } = computeNativeInvoiceTotals({
        lines: state.lines,
        tax: state.tax,
        taxMode: state.taxMode,
        discount: state.discount,
        discountMode: state.discountMode,
        deposit: state.deposit,
        depositMode: state.depositMode,
        isTaxPerLineItem: state.isTaxPerLineItem,
        isDiscountPerLineItem: state.isDiscountPerLineItem,
        isDepositPerLineItem: state.isDepositPerLineItem,
    });

    const customerEmail = state.customer?.email ?? "";
    const toEmails = parseRecipientList(state.emailTo);
    const ccEmails = parseRecipientList(state.emailCc, toEmails);
    const bccEmails = parseRecipientList(state.emailBcc, [
        ...toEmails,
        ...ccEmails,
    ]);

    const trimmedEmailSubject = state.emailSubject.trim().slice(0, 255);
    const fromName = state.from.businessName;

    // Every From/To field is always editable; a piece appears on the invoice
    // whenever it has a value, with empty strings normalized to null.
    const fromParty = {
        businessName: state.from.businessName || null,
        name: state.from.name || null,
        email: EMAIL_PATTERN.test(state.from.email) ? state.from.email : null,
        phoneNumber: state.from.phoneNumber || null,
        taxId: state.from.taxId || null,
        address: {
            street: state.from.street || null,
            street2: state.from.street2 || null,
            city: state.from.city || null,
            state: state.from.state || null,
            postalCode: state.from.postalCode || null,
            country: state.from.country || null,
        },
    };

    return {
        counterpartyId: nonEmptyIdOrUndefined(state.customer?.id) ?? null,
        status: InvoiceStatus.DRAFT,
        renderSnapshot: {
            from: fromParty,
            to: {
                businessName: invoiceToPartyBusinessName({
                    displayName: state.customer?.name,
                    company: state.toCompany,
                }),
                company: nonEmptyStringOrNull(state.toCompany),
                name: state.toFullName || null,
                email: EMAIL_PATTERN.test(customerEmail) ? customerEmail : null,
                phoneNumber: nonEmptyStringOrNull(state.customer?.phoneNumber),
                taxId: state.toTaxId || null,
                address: {
                    street: nonEmptyStringOrNull(state.customer?.street),
                    street2: nonEmptyStringOrNull(state.customer?.street2),
                    city: nonEmptyStringOrNull(state.customer?.city),
                    state: isKnownNonUsInvoiceCountry(state.customer?.country)
                        ? null
                        : nonEmptyStringOrNull(state.customer?.state),
                    postalCode: nonEmptyStringOrNull(
                        state.customer?.postalCode,
                    ),
                    country: nonEmptyStringOrNull(state.customer?.country),
                },
            },
            invoiceNumber: state.invoiceNumber || null,
            invoiceDate: state.invoiceDate,
            dueDate: isTemplateMode ? null : state.dueDate || null,
            // Templates never carry a PO number (invoice-only field): the
            // field is hidden in the template's Details panel, and the
            // server strips it regardless — omit it defensively so the
            // client payload matches what's actually persisted.
            poNumber: isTemplateMode ? null : state.poNumber || null,
            note: state.note || null,
            terms: state.terms || null,
            footer: state.footer || null,
            footerAlign: state.footerAlign,
            showPagination: state.showPagination,
            pageSize: state.pageSize,
            currency: "usd",
            subtotal,
            // Per-line flags are mutually exclusive with a non-zero header
            // value. Put computed dollars on each line; the splitter emits
            // TAX/DISCOUNT/DEPOSIT rows from those amounts instead of a
            // header-level spread.
            tax: state.isTaxPerLineItem ? 0 : taxAmount,
            discount: state.isDiscountPerLineItem ? 0 : discountAmount,
            deposit: state.isDepositPerLineItem ? 0 : depositAmount,
            isTaxPerLineItem: state.isTaxPerLineItem,
            isDiscountPerLineItem: state.isDiscountPerLineItem,
            isDepositPerLineItem: state.isDepositPerLineItem,
            // Display-per-line-item only makes sense once calculation is also
            // per-line item — force it off here so a stale display flag from
            // before the matching isXPerLineItem was turned off can never be
            // saved alongside a header-level calculation (the render-snapshot
            // refine also rejects this combination at the request boundary).
            isTaxDisplayedPerLineItem:
                state.isTaxPerLineItem && state.isTaxDisplayedPerLineItem,
            isDiscountDisplayedPerLineItem:
                state.isDiscountPerLineItem &&
                state.isDiscountDisplayedPerLineItem,
            isDepositDisplayedPerLineItem:
                state.isDepositPerLineItem &&
                state.isDepositDisplayedPerLineItem,
            taxMode: state.taxMode,
            discountMode: state.discountMode,
            depositMode: state.depositMode,
            taxSign,
            discountSign,
            depositSign,
            taxInput: state.isTaxPerLineItem ? 0 : state.tax,
            discountInput: state.isDiscountPerLineItem ? 0 : state.discount,
            depositInput: state.isDepositPerLineItem ? 0 : state.deposit,
            total,
            lines: state.lines.map((line): NativeInvoiceLineRenderSnapshot => {
                const lineAmount = computeNativeInvoiceLineAmount(line);
                return {
                    id: line.id,
                    catalogItemId: line.catalogItemId,
                    catalogItemPriceId: line.catalogItemPriceId,
                    productOrService: line.productOrService || null,
                    description: line.description || null,
                    quantity: line.quantity,
                    unitAmount: line.unitAmount,
                    amount: lineAmount,
                    discount: state.isDiscountPerLineItem
                        ? storedLineAdjustmentFromLabeled({
                              labeledValue: line.discount,
                              mode: state.discountMode,
                              lineAmount,
                          })
                        : 0,
                    tax: state.isTaxPerLineItem
                        ? storedLineAdjustmentFromLabeled({
                              labeledValue: line.tax,
                              mode: state.taxMode,
                              lineAmount,
                          })
                        : 0,
                    deposit: state.isDepositPerLineItem
                        ? storedLineAdjustmentFromLabeled({
                              labeledValue: line.deposit,
                              mode: state.depositMode,
                              lineAmount,
                          })
                        : 0,
                    discountMode: state.isDiscountPerLineItem
                        ? state.discountMode
                        : null,
                    taxMode: state.isTaxPerLineItem ? state.taxMode : null,
                    depositMode: state.isDepositPerLineItem
                        ? state.depositMode
                        : null,
                    serviceDate: null,
                    sku: null,
                    billableExpenseTransactionId:
                        line.billableExpenseTransactionId,
                };
            }),
            initials: getInitials(fromName),
            logoPath: state.logoPath,
            backgroundColor: state.backgroundColor,
            accentColor: state.accentColor,
            textColor: state.textColor,
            fontFamily: toSnapshotFontFamily(state.fontFamily),
            labelOverrides: state.labelOverrides,
            fieldSettings: state.fieldSettings,
        },
        adjustmentSettings: state.adjustmentSettings,
        lineAccounting: buildNativeInvoiceLineAccounting({
            lines: state.lines,
            primaryAccrualLedgerId,
            incomeAccountLedgerId,
            baselineByLineId: lineCodingBaselineByLineId,
        }),
        // Always sent, even with no recipient or subject yet: the server clears
        // every stored email column when the snapshot is omitted, so gating this
        // would drop the composed subject/message/CC/BCC on save. Sending is
        // gated separately (the send action requires a recipient).
        emailSnapshot: {
            to: toEmails.join(", "),
            replyTo: EMAIL_PATTERN.test(state.replyToEmail)
                ? state.replyToEmail
                : null,
            ...(ccEmails.length ? { cc: ccEmails } : {}),
            ...(bccEmails.length ? { bcc: bccEmails } : {}),
            subject: trimmedEmailSubject,
            message: state.emailMessage,
            attachPdf: state.emailAttachPdf,
            // A date-only value (no time picked yet) is not a schedule — it
            // would otherwise persist as midnight.
            scheduledSendAt:
                state.scheduleEnabled && state.scheduledSendAt.includes("T")
                    ? toIsoStringOrNull(state.scheduledSendAt)
                    : null,
            sendingEmailId: state.sendingEmailId,
        },
        sendingEmailId: state.sendingEmailId,
        termType: state.termType,
        customTerm:
            state.termType === NativeInvoiceTermType.CUSTOM
                ? state.customTerm
                : null,
        isEmailSubjectAutomatic: state.isEmailSubjectAutomatic,
        isEmailMessageAutomatic: state.isEmailMessageAutomatic,
        collectPayment: state.collectPayment,
        stripeFinancialAccountId: state.collectPayment
            ? state.stripeFinancialAccountId
            : null,
        ...(state.collectPayment
            ? { acceptedPaymentMethods: state.acceptedPaymentMethods }
            : {}),
    };
}

export function toNativeInvoiceUpdateBody(
    draft: NativeInvoiceDraftBody,
): NativeInvoiceUpdateBody {
    return {
        renderSnapshot: draft.renderSnapshot,
        emailSnapshot: draft.emailSnapshot,
        counterpartyId: draft.counterpartyId,
        lineAccounting: draft.lineAccounting,
        adjustmentSettings: draft.adjustmentSettings,
        termType: draft.termType,
        customTerm: draft.customTerm,
        isEmailSubjectAutomatic: draft.isEmailSubjectAutomatic,
        isEmailMessageAutomatic: draft.isEmailMessageAutomatic,
        // Payment settings must round-trip on every save, not just create:
        // omitting them here silently drops any post-create edit to Collect
        // payment / the Stripe destination account (the PATCH body simply
        // wouldn't carry the change, even though the backend fully supports
        // updating these on both DRAFT and OPEN via the COLLECT_PAYMENT
        // capability).
        collectPayment: draft.collectPayment,
        stripeFinancialAccountId: draft.stripeFinancialAccountId,
        acceptedPaymentMethods: draft.acceptedPaymentMethods,
    };
}
